import { FC, HTMLAttributes, forwardRef } from 'react'
import styled from 'styled-components'

import {
  Button,
  Carat,
  RotateClockwise,
  SelectMark,
  TypeStyle,
  fromMd,
  untilMd,
} from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../app/_providers/TranslationProvider.client'

/**
 *
 *  A wrapper for the embroidery modal header components
 */
const Wrapper: FC<HTMLAttributes<HTMLDivElement>> = ({ children, ...rest }) => {
  return <Wrap {...rest}>{children}</Wrap>
}

/**
 *
 * A section for the embroidery modal header components
 */
const Section: FC<HTMLAttributes<HTMLDivElement>> = ({ children, ...rest }) => {
  return <SectionWrap {...rest}>{children}</SectionWrap>
}

/**
 *
 * A styled back button for the embroidery modal header.
 *
 * @param props
 * @param props.handleClick - Callback on button click
 */

export type EmbroideryModalHeaderBackButtonProps = {
  handleClick: () => void
} & HTMLAttributes<HTMLButtonElement>

const BackButton: FC<EmbroideryModalHeaderBackButtonProps> = ({ children, handleClick }) => {
  return (
    <StyledBackButton
      aria-label='back'
      borderless={true}
      variant='ghost'
      size='small'
      onClick={handleClick}
    >
      <Carat rotateClockwise={RotateClockwise.Half} />
      {children}
    </StyledBackButton>
  )
}

/**
 *
 * A styled title component for the embroidery modal header
 */

const Title: FC<{ children?: React.ReactNode }> = ({ children }) => {
  return (
    <TitleWrap>
      <TitleText>{children}</TitleText>
    </TitleWrap>
  )
}

/**
 * A back button for the embroidery modal header.
 * @param props
 * @param props.onClick - Callback on button click
 * @param props.type - The type of button to render mobile or desktop defaults to mobile
 * @param props.text - Text displayed after close icon
 */
export type EmbroideryModalHeaderCloseButtonProps = {
  onClick: () => void
  text?: string
} & HTMLAttributes<HTMLButtonElement>

const CloseButtonMobile = forwardRef<HTMLButtonElement, EmbroideryModalHeaderCloseButtonProps>(
  ({ onClick, text }, ref) => {
    const {
      embroidery: { ariaLabelCloseModal },
    } = useTranslation()

    return (
      <MobileCloseButton
        ref={ref}
        aria-label={ariaLabelCloseModal}
        borderless={true}
        variant='ghost'
        size='small'
        onClick={onClick}
      >
        <SelectMark /> <ButtonText>{text}</ButtonText>
      </MobileCloseButton>
    )
  }
)

const CloseButtonDesktop = forwardRef<HTMLButtonElement, EmbroideryModalHeaderCloseButtonProps>(
  ({ children, onClick, text }, ref) => {
    const {
      embroidery: { ariaLabelCloseModal },
    } = useTranslation()

    return (
      <DesktopCloseButton
        variant='black-on-white'
        onClick={onClick}
        ref={ref}
        aria-label={ariaLabelCloseModal}
      >
        <SelectMark /> <ButtonText>{text}</ButtonText>
        {children}
      </DesktopCloseButton>
    )
  }
)

export const EmbroideryModalHeader = {
  Wrapper,
  Section,
  BackButton,
  Title,
  CloseButtonMobile,
  CloseButtonDesktop,
}

const Wrap = styled.header`
  display: flex;
  padding: ${o => o.theme.spacing(3)} 0;
  position: sticky;
  top: 0;
  z-index: ${o => o.theme.zIndex.foregroundContent + 1};
  ${untilMd} {
    background-color: ${o => o.theme.color.fill.background};
  }
`

const SectionWrap = styled.div`
  flex: 1;

  &:last-child {
    display: flex;
    justify-content: flex-end;
    ${fromMd} {
      display: none;
    }
  }
`

const TitleText = styled.h1`
  ${TypeStyle.css.headlineExtraExtraSmall}
  text-transform: uppercase;
  ${o => o.theme.typography.effect.uppercase};
`

const TitleWrap = styled.div`
  padding-top: ${o => o.theme.spacing(1)};
  text-align: center;
  flex: 4;
  ${fromMd} {
    display: none;
  }
`

const StyledBackButton = styled(Button)`
  padding-left: ${o => o.theme.spacing(4)};
  ${fromMd} {
    background-color: ${o => o.theme.color.fill.subtle};
    display: none;
  }
`
const MobileCloseButton = styled(Button)`
  background-color: ${o => o.theme.color.fill.background};
  padding-right: ${o => o.theme.spacing(4)};
  svg {
    height: ${o => o.theme.spacing(4)};
    width: ${o => o.theme.spacing(4)};
  }
  path {
    stroke-width: 1;
  }

  ${fromMd} {
    padding: ${o => o.theme.spacing(2, 4, 2, 4)};
    border: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  }
`

const DesktopCloseButton = styled(MobileCloseButton)`
  ${untilMd} {
    display: none;
  }
`

const ButtonText = styled.span`
  padding-left: ${o => o.theme.spacing(2)};
`
